'use client'

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loader2, Check, X, Plus, Copy, RefreshCw, CheckCircle2 } from 'lucide-react'
import { whiteLabelService, WhiteLabelDomain } from '@/services/whiteLabelService'
import { useAuthStore } from '@/stores/authStore'

export default function WhiteLabelDomains() {
    const { user } = useAuthStore()
    const queryClient = useQueryClient()
    const [domains, setDomains] = useState<WhiteLabelDomain[]>([])
    const [isAddingDomain, setIsAddingDomain] = useState(false)
    const [newDomain, setNewDomain] = useState('')

    // Fetch merchant domains
    const { isLoading } = useQuery({
        queryKey: ['merchant-domains', user?.merchant],
        queryFn: async () => {
            if (!user?.merchant) throw new Error('No merchant found')
            const result = await whiteLabelService.getMerchantDomains(user.merchant)
            setDomains(result)
            return result
        },
        enabled: !!user?.merchant,
    })

    // Create domain mutation
    const createDomainMutation = useMutation({
        mutationFn: async () => {
            if (!user?.merchant) throw new Error('No merchant found')
            return whiteLabelService.createDomain({
                merchant: user.merchant,
                domain: newDomain,
                is_active: true,
                ssl_enabled: true,
            })
        },
        onSuccess: (data) => {
            toast.success(`Domain ${data.domain} created successfully`)
            setNewDomain('')
            setIsAddingDomain(false)
            queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to create domain')
        },
    })

    // Verify domain mutation
    const verifyDomainMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.verifyDomain(domainId, 'dns'),
        onSuccess: (data, _domainId) => {
            if (data.is_verified) {
                toast.success('Domain verified successfully!')
                queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
            } else {
                toast.error(data.message || 'Domain verification failed. Please check your DNS records.')
            }
        },
        onError: (error: any) => {
            toast.error(error.message || 'Verification failed')
        },
    })

    // Generate verification code mutation
    const generateCodeMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.generateVerificationCode(domainId),
        onSuccess: (data) => {
            toast.success('Verification code generated')
            // Copy to clipboard
            const code = data.verification_code
            navigator.clipboard.writeText(code)
            toast.success('Verification code copied to clipboard!')
            queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to generate verification code')
        },
    })

    // Set primary domain mutation
    const setPrimaryMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.setDomainAsPrimary(domainId),
        onSuccess: () => {
            toast.success('Primary domain updated')
            queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to set primary domain')
        },
    })

    // Toggle domain active mutation
    const toggleActiveMutation = useMutation({
        mutationFn: ({ domainId, isActive }: { domainId: string; isActive: boolean }) =>
            whiteLabelService.toggleDomainActive(domainId, isActive),
        onSuccess: () => {
            toast.success('Domain status updated')
            queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to update domain')
        },
    })

    // Delete domain mutation
    const deleteDomainMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.deleteDomain(domainId),
        onSuccess: () => {
            toast.success('Domain deleted successfully')
            queryClient.invalidateQueries({ queryKey: ['merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to delete domain')
        },
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="animate-spin" size={32} />
            </div>
        )
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">White-Label Domains</h1>
                    <p className="text-gray-600 mt-2">
                        Manage your custom CNAME domains for white-label branding
                    </p>
                </div>
                <Button
                    onClick={() => setIsAddingDomain(!isAddingDomain)}
                    className="flex items-center gap-2"
                >
                    <Plus size={18} /> Add Domain
                </Button>
            </div>

            {/* Add Domain Form */}
            {isAddingDomain && (
                <Card className="p-6 border-blue-200 bg-blue-50">
                    <h3 className="text-lg font-semibold mb-4">Create New Domain</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Domain Name</label>
                            <Input
                                placeholder="e.g., payments.yourdomain.com"
                                value={newDomain}
                                onChange={(e) => setNewDomain(e.target.value)}
                                className="w-full"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                Enter your custom CNAME domain (e.g., payments.acme.com)
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => createDomainMutation.mutate()}
                                disabled={!newDomain || createDomainMutation.isPending}
                                className="flex items-center gap-2"
                            >
                                {createDomainMutation.isPending && <Loader2 className="animate-spin" size={16} />}
                                Create Domain
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setIsAddingDomain(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Domains List */}
            {domains.length === 0 ? (
                <Card className="p-12 text-center">
                    <p className="text-gray-600 mb-4">No domains configured yet</p>
                    <Button
                        onClick={() => setIsAddingDomain(true)}
                        className="flex items-center gap-2 mx-auto"
                    >
                        <Plus size={18} /> Create Your First Domain
                    </Button>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {domains.map((domain) => (
                        <Card
                            key={domain.id}
                            className={`p-6 border-l-4 ${domain.is_primary
                                ? 'border-l-blue-500 bg-blue-50'
                                : domain.is_verified
                                    ? 'border-l-green-500'
                                    : 'border-l-yellow-500'
                                }`}
                        >
                            <div className="space-y-4">
                                {/* Domain Header */}
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold flex items-center gap-2">
                                            {domain.domain}
                                            {domain.is_primary && (
                                                <Badge className="bg-blue-500">Primary</Badge>
                                            )}
                                            {domain.is_verified && (
                                                <CheckCircle2 className="text-green-500" size={20} />
                                            )}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Status: {domain.is_active ? 'Active' : 'Inactive'} • SSL:{' '}
                                            {domain.ssl_enabled ? 'Enabled' : 'Disabled'}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        {!domain.is_verified && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => generateCodeMutation.mutate(domain.id)}
                                                disabled={generateCodeMutation.isPending}
                                                className="flex items-center gap-1"
                                            >
                                                {generateCodeMutation.isPending ? (
                                                    <Loader2 className="animate-spin" size={14} />
                                                ) : (
                                                    <RefreshCw size={14} />
                                                )}
                                                Generate Code
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Verification Status */}
                                <div className="space-y-2 bg-white p-4 rounded">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Verification</span>
                                        <div className="flex items-center gap-2">
                                            {domain.is_verified ? (
                                                <>
                                                    <Check className="text-green-500" size={18} />
                                                    <span className="text-sm text-green-600">Verified on {domain.verified_at?.split('T')[0]}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <X className="text-yellow-500" size={18} />
                                                    <span className="text-sm text-yellow-600">Not verified</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {!domain.is_verified && domain.verification_code && (
                                        <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                                            <p className="text-xs font-medium text-yellow-800 mb-2">
                                                Add this CNAME record to your DNS:
                                            </p>
                                            <div className="space-y-1 text-xs text-gray-700 font-mono">
                                                <div>
                                                    <strong>Name:</strong> {domain.domain}
                                                </div>
                                                <div>
                                                    <strong>Type:</strong> CNAME
                                                </div>
                                                <div>
                                                    <strong>Value:</strong> pexiblocks.com
                                                </div>
                                                <div>
                                                    <strong>TTL:</strong> 3600
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(
                                                        `${domain.domain} CNAME pexiblocks.com`
                                                    )
                                                    toast.success('DNS record copied!')
                                                }}
                                                className="mt-2 w-full text-xs"
                                            >
                                                <Copy size={12} /> Copy DNS Record
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex flex-wrap gap-2 pt-2">
                                    {!domain.is_verified && (
                                        <Button
                                            size="sm"
                                            className="flex items-center gap-1"
                                            onClick={() => verifyDomainMutation.mutate(domain.id)}
                                            disabled={verifyDomainMutation.isPending}
                                        >
                                            {verifyDomainMutation.isPending && (
                                                <Loader2 className="animate-spin" size={14} />
                                            )}
                                            Verify Domain
                                        </Button>
                                    )}

                                    {domain.is_verified && !domain.is_primary && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setPrimaryMutation.mutate(domain.id)}
                                            disabled={setPrimaryMutation.isPending}
                                            className="flex items-center gap-1"
                                        >
                                            {setPrimaryMutation.isPending && (
                                                <Loader2 className="animate-spin" size={14} />
                                            )}
                                            Set as Primary
                                        </Button>
                                    )}

                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                            toggleActiveMutation.mutate({
                                                domainId: domain.id,
                                                isActive: !domain.is_active,
                                            })
                                        }
                                        disabled={toggleActiveMutation.isPending}
                                    >
                                        {domain.is_active ? 'Disable' : 'Enable'}
                                    </Button>

                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => {
                                            if (confirm(`Are you sure you want to delete ${domain.domain}?`)) {
                                                deleteDomainMutation.mutate(domain.id)
                                            }
                                        }}
                                        disabled={deleteDomainMutation.isPending}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Instructions */}
            <Card className="p-6 bg-blue-50 border-blue-200">
                <h3 className="font-semibold mb-3 text-blue-900">How to Set Up Your CNAME Domain</h3>
                <ol className="space-y-2 text-sm text-blue-800">
                    <li>
                        <strong>1. Create Domain:</strong> Add your custom domain using the form above (e.g., payments.acme.com)
                    </li>
                    <li>
                        <strong>2. Update DNS:</strong> Log into your domain provider (GoDaddy, Namecheap, etc.) and add a CNAME record pointing to pexiblocks.com
                    </li>
                    <li>
                        <strong>3. Wait for Propagation:</strong> DNS changes can take 5-30 minutes to propagate globally
                    </li>
                    <li>
                        <strong>4. Verify Domain:</strong> Click "Verify Domain" once your DNS record is configured
                    </li>
                    <li>
                        <strong>5. Set Primary:</strong> Once verified, set it as your primary domain
                    </li>
                    <li>
                        <strong>6. Access Your Portal:</strong> Visit https://{domains[0].domain} to access your white-label portal
                    </li>
                </ol>
            </Card>
        </div>
    )
}
